import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  EMPLOYEE_STATUS_COLORS,
  formatEnumLabel,
} from '../../../../constants/hrm';
import {
  useEmployees,
  useDepartments,
  usePositions,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from '../../../../hooks/useHRM';

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  department_id: '',
  position_id: '',
  employment_type: 'FULL_TIME',
  hire_date: '',
  status: 'ACTIVE',
  employee_id_number: '',
};

const EmployeeList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmploymentType, setFilterEmploymentType] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // React Query hooks
  const filters = {
    page,
    per_page: 20,
    ...(search && { search }),
    ...(filterDepartment && { department_id: filterDepartment }),
    ...(filterStatus && { status: filterStatus }),
    ...(filterEmploymentType && { employment_type: filterEmploymentType }),
  };

  const { data: employeeData, isLoading: loading } = useEmployees(chatbotId, filters);
  const { data: departmentData } = useDepartments(chatbotId, { per_page: 1000 });
  const { data: positionData } = usePositions(chatbotId, { per_page: 1000 });

  const employees = employeeData?.data || [];
  const pagination = employeeData?.pagination || { total: 0, pages: 1, per_page: 20 };
  const departments = departmentData?.data || [];
  const positions = positionData?.data || [];

  const createEmployee = useCreateEmployee(chatbotId);
  const updateEmployee = useUpdateEmployee(chatbotId);
  const deleteEmployee = useDeleteEmployee(chatbotId);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const clearFilters = () => {
    setSearch('');
    setFilterDepartment('');
    setFilterStatus('');
    setFilterEmploymentType('');
    setPage(1);
  };

  const handleOpenCreate = () => {
    setEditingEmployee(null);
    setFormData(initialFormData);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (employee, e) => {
    if (e) e.stopPropagation();
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department_id: employee.department_id || '',
      position_id: employee.position_id || '',
      employment_type: employee.employment_type || 'FULL_TIME',
      hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
      status: employee.status || 'ACTIVE',
      employee_id_number: employee.employee_id_number || '',
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = { ...formData };

    // Convert empty strings to null for optional fields
    if (!payload.department_id) payload.department_id = null;
    if (!payload.position_id) payload.position_id = null;
    if (!payload.phone) payload.phone = null;
    if (!payload.hire_date) payload.hire_date = null;
    if (!payload.employee_id_number) payload.employee_id_number = null;

    try {
      if (editingEmployee) {
        await updateEmployee.mutateAsync({ id: editingEmployee.id, ...payload });
      } else {
        await createEmployee.mutateAsync(payload);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving employee:', error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    }
  };

  const handleOpenDelete = (employee, e) => {
    if (e) e.stopPropagation();
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;
    try {
      await deleteEmployee.mutateAsync(employeeToDelete.id);
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
    } catch (error) {
      console.error('Error deleting employee:', error);
    }
  };

  const handleRowClick = (employee) => {
    navigate(`/chatbot/${chatbotId}/hrm/employees/${employee.id}`);
  };

  const saving = createEmployee.isPending || updateEmployee.isPending;
  const deleting = deleteEmployee.isPending;

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          You do not have permission to view employees.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Employees
        </Typography>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} size="small">
            Add Employee
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search employees..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={filterDepartment}
                onChange={(e) => { setFilterDepartment(e.target.value); setPage(1); }}
                label="Department"
              >
                <MenuItem value="">All</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {EMPLOYEE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatEnumLabel(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={filterEmploymentType}
                onChange={(e) => { setFilterEmploymentType(e.target.value); setPage(1); }}
                label="Employment Type"
              >
                <MenuItem value="">All</MenuItem>
                {EMPLOYMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {formatEnumLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Tooltip title="Clear filters">
              <IconButton onClick={clearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Employee Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Employment Type</TableCell>
                  <TableCell>Hire Date</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length > 0 ? (
                  employees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(employee)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {employee.first_name} {employee.last_name}
                          </Typography>
                          {employee.employee_id_number && (
                            <Typography variant="caption" color="textSecondary">
                              ({employee.employee_id_number})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell>{employee.department_name || '-'}</TableCell>
                      <TableCell>{employee.position_title || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatEnumLabel(employee.status)}
                          color={EMPLOYEE_STATUS_COLORS[employee.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatEnumLabel(employee.employment_type) || '-'}
                      </TableCell>
                      <TableCell>
                        {employee.hire_date
                          ? new Date(employee.hire_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenEdit(employee, e)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleOpenDelete(employee, e)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={canModify ? 8 : 7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No employees found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEmployee ? 'Edit Employee' : 'Create Employee'}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleFormChange('first_name')}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleFormChange('last_name')}
                error={!!formErrors.last_name}
                helperText={formErrors.last_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleFormChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleFormChange('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID Number"
                value={formData.employee_id_number}
                onChange={handleFormChange('employee_id_number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department_id}
                  onChange={handleFormChange('department_id')}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={formData.position_id}
                  onChange={handleFormChange('position_id')}
                  label="Position"
                >
                  <MenuItem value="">None</MenuItem>
                  {positions
                    .filter((pos) =>
                      formData.department_id
                        ? pos.department_id === formData.department_id
                        : true
                    )
                    .map((pos) => (
                      <MenuItem key={pos.id} value={pos.id}>
                        {pos.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employment_type}
                  onChange={handleFormChange('employment_type')}
                  label="Employment Type"
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatEnumLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleFormChange('status')}
                  label="Status"
                >
                  {EMPLOYEE_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatEnumLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={formData.hire_date}
                onChange={handleFormChange('hire_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : editingEmployee ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>
              {employeeToDelete?.first_name} {employeeToDelete?.last_name}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeList;
